import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  BadgeCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Home,
  MapPin,
  Send,
  Sparkles,
  Star,
  X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { aiApi, experienceApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import type { Experience } from "@/types";

const money = (n: number) => `₹${n.toLocaleString("en-IN")}`;
function fmtDuration(m: number) {
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h && min) return `${h}h ${min}m`;
  if (h) return `${h} hr${h > 1 ? "s" : ""}`;
  return `${m} min`;
}

export default function ExperienceDetail() {
  const { id } = useParams<{ id: string }>();
  const [exp, setExp] = useState<Experience | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    experienceApi
      .get(id)
      .then((r) => setExp(r.data))
      .catch((e) => setError(errorMessage(e, "Experience not found")));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="container-page grid min-h-[60vh] place-items-center pt-24 text-center">
          <div>
            <p className="font-display text-xl font-semibold text-navy-900">{error}</p>
            <Link to="/experiences" className="btn-primary mt-4">Back to Experiences</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!exp) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="container-page pt-28">
          <div className="h-80 animate-pulse rounded-3xl bg-white" />
        </div>
      </div>
    );
  }

  const discounted = exp.original_price && exp.original_price > exp.price;

  return (
    <div className="min-h-screen bg-cream pb-28">
      <Navbar />

      <div className="container-page pt-24">
        {/* breadcrumb */}
        <nav className="mb-4 flex items-center gap-1.5 text-sm text-slate-500">
          <Link to="/" className="flex items-center gap-1 hover:text-iris-600"><Home size={13} /> Home</Link>
          <ChevronRight size={13} />
          <Link to="/experiences" className="hover:text-iris-600">Experiences</Link>
          <ChevronRight size={13} />
          <span className="truncate text-navy-800">{exp.title}</span>
        </nav>

        {/* title row */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-1.5">
            {exp.tags.map((t) => (
              <span key={t} className="rounded-full bg-iris-50 px-2.5 py-0.5 text-xs font-semibold text-iris-600">{t}</span>
            ))}
          </div>
          <h1 className="mt-2 font-display text-3xl font-bold text-navy-900 sm:text-4xl">{exp.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="flex items-center gap-1 font-semibold text-navy-800">
              <Star size={15} className="fill-gold-500 text-gold-500" /> {exp.rating.toFixed(1)}
              <span className="font-normal text-slate-400">({exp.reviews_count} reviews)</span>
            </span>
            <span className="flex items-center gap-1"><Clock size={14} /> {fmtDuration(exp.duration_minutes)}</span>
            <span className="flex items-center gap-1"><MapPin size={14} className="text-iris-500" /> {exp.distance_km.toFixed(1)} km away · {exp.city}</span>
          </div>
        </div>

        <Gallery images={exp.images.length ? exp.images : exp.image_url ? [exp.image_url] : []} title={exp.title} />

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          {/* left: story */}
          <div className="space-y-8">
            <section>
              <h2 className="mb-2 font-display text-xl font-bold text-navy-900">About this experience</h2>
              <p className="leading-relaxed text-slate-600">{exp.description}</p>
            </section>

            {exp.highlights.length > 0 && (
              <section>
                <h2 className="mb-3 font-display text-xl font-bold text-navy-900">What to expect</h2>
                <ol className="relative space-y-4 border-l-2 border-slate-200 pl-6">
                  {exp.highlights.map((h, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-[31px] grid h-6 w-6 place-items-center rounded-full bg-iris-500 text-xs font-bold text-white">
                        {i + 1}
                      </span>
                      <p className="text-sm text-navy-800">{h}</p>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            <section className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <h3 className="mb-2 font-display text-base font-semibold text-navy-900">What's included</h3>
                <ul className="space-y-1.5">
                  {exp.included.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check size={16} className="mt-0.5 shrink-0 text-jade-500" /> {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="mb-2 font-display text-base font-semibold text-navy-900">Not included</h3>
                <ul className="space-y-1.5">
                  {exp.excluded.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm text-slate-600">
                      <X size={16} className="mt-0.5 shrink-0 text-red-400" /> {x}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* host */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="mb-3 font-display text-base font-semibold text-navy-900">Meet your host</h2>
              <div className="flex items-start gap-4">
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-gradient-to-br from-gold-400 to-iris-500 text-xl font-bold text-white">
                  {exp.host_name.charAt(0)}
                </span>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-navy-900">{exp.host_name}</span>
                    {exp.host_verified && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-jade-50 px-1.5 py-0.5 text-[11px] font-semibold text-jade-600">
                        <BadgeCheck size={12} /> Verified
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                    <Star size={12} className="fill-gold-500 text-gold-500" /> {exp.host_rating.toFixed(1)} host rating
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{exp.host_bio}</p>
                </div>
              </div>
            </section>
          </div>

          {/* right: price summary (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
              <div className="flex items-end gap-2">
                <span className="font-display text-2xl font-bold text-navy-900">{money(exp.price)}</span>
                {discounted && <span className="mb-1 text-sm text-slate-400 line-through">{money(exp.original_price as number)}</span>}
                <span className="mb-1 text-xs text-slate-500">/ person</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">Free cancellation up to 24h before.</p>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-slate-500">
                  <Sparkles size={14} className="text-iris-500" /> Have a question? Ask Maayad AI below.
                </div>
              </div>
              <button onClick={() => setAiOpen(true)} className="btn-outline mt-3 w-full">
                <Sparkles size={16} /> Ask about this experience
              </button>
            </div>
          </aside>
        </div>
      </div>

      {/* Sticky booking bar */}
      <BookingBar exp={exp} />

      {/* Floating Maayad AI button + panel */}
      <button
        onClick={() => setAiOpen((v) => !v)}
        className="fixed bottom-36 right-5 z-40 grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-gold-400 to-iris-500 text-white shadow-iris transition hover:scale-105 sm:bottom-24 lg:bottom-6"
        aria-label="Ask Maayad AI"
      >
        {aiOpen ? <X size={22} /> : <Sparkles size={24} />}
      </button>
      {aiOpen && <AIPanel exp={exp} onClose={() => setAiOpen(false)} />}
    </div>
  );
}

/* ------------------------------- gallery -------------------------------- */
function Gallery({ images, title }: { images: string[]; title: string }) {
  const [idx, setIdx] = useState(0);
  const has = images.length > 0;
  const go = (d: number) => setIdx((i) => (i + d + images.length) % images.length);

  return (
    <div className="relative h-64 w-full overflow-hidden rounded-3xl bg-slate-100 sm:h-96">
      {has ? (
        <img src={images[idx]} alt={title} className="h-full w-full object-cover" />
      ) : (
        <div className="grid h-full place-items-center text-slate-300">No image</div>
      )}
      {images.length > 1 && (
        <>
          <button onClick={() => go(-1)} aria-label="Previous" className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-navy-800 shadow transition hover:bg-white">
            <ChevronLeft size={20} />
          </button>
          <button onClick={() => go(1)} aria-label="Next" className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-navy-800 shadow transition hover:bg-white">
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
            {images.map((_, i) => (
              <span key={i} className={`h-2 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-2 bg-white/60"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------------------- sticky booking ---------------------------- */
function BookingBar({ exp }: { exp: Experience }) {
  const [date, setDate] = useState("");
  const [guests, setGuests] = useState(2);
  const [booked, setBooked] = useState(false);
  const total = exp.price * guests;

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur">
      <div className="container-page flex flex-col gap-3 py-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-transparent text-sm text-navy-900 focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <span className="text-slate-500">Guests</span>
            <select value={guests} onChange={(e) => setGuests(Number(e.target.value))} className="bg-transparent font-medium text-navy-900 focus:outline-none">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 sm:ml-auto">
          <div className="text-right">
            <div className="text-xs text-slate-500">Total</div>
            <div className="font-display text-lg font-bold text-navy-900">{money(total)}</div>
          </div>
          <button onClick={() => setBooked(true)} className="btn-primary px-6 py-3 text-base">
            Book Now
          </button>
        </div>
      </div>
      {booked && (
        <div className="border-t border-jade-200 bg-jade-50 py-2 text-center text-sm text-jade-700">
          🎉 Reserved (demo) — {guests} guest{guests > 1 ? "s" : ""}
          {date ? ` on ${new Date(date).toLocaleDateString("en-IN")}` : ""}. Real payment & inventory are the next step.
        </div>
      )}
    </div>
  );
}

/* ------------------------------- AI panel ------------------------------- */
function AIPanel({ exp, onClose }: { exp: Experience; onClose: () => void }) {
  const [messages, setMessages] = useState<{ role: "user" | "ai"; text: string }[]>([
    { role: "ai", text: `Hi! Ask me anything about "${exp.title}" — accessibility, food, what to bring, timing…` },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const context = `Experience: ${exp.title}. Category: ${exp.category}. Duration: ${fmtDuration(
    exp.duration_minutes,
  )}. Price: ${money(exp.price)}. Included: ${exp.included.join(", ") || "none"}. Not included: ${
    exp.excluded.join(", ") || "none"
  }. Details: ${exp.description}`;

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await aiApi.chat(`${context}\n\nTraveller question: ${q}`);
      setMessages((m) => [...m, { role: "ai", text: res.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I couldn't answer that right now." }]);
    } finally {
      setLoading(false);
    }
  };

  const SUGGESTIONS = ["Is it wheelchair accessible?", "Is vegetarian food available?", "What should I bring?"];

  return (
    <div className="fixed bottom-[13rem] right-5 z-40 flex h-[26rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lift sm:bottom-40 lg:bottom-24">
      <div className="flex items-center justify-between bg-navy-900 px-4 py-3 text-white">
        <span className="flex items-center gap-2 font-display text-sm font-semibold">
          <Sparkles size={15} className="text-gold-400" /> Ask about this experience
        </span>
        <button onClick={onClose} aria-label="Close"><X size={18} /></button>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "ml-auto bg-iris-500 text-white" : "bg-cream text-navy-900"}`}>
            {m.text}
          </div>
        ))}
        {loading && <div className="w-fit rounded-lg bg-cream px-3 py-2 text-sm text-slate-400">Thinking…</div>}
        {messages.length <= 1 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => setInput(s)} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-navy-700 hover:bg-cream">
                {s}
              </button>
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-slate-100 p-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question…" className="input" />
        <button className="btn-primary px-3" aria-label="Send"><Send size={16} /></button>
      </form>
    </div>
  );
}